import { useI18n, useT } from '@kovix/shell-kit/i18n';
import { type RealtimeMessage, realtimeDestinations, useRealtime, useRealtimeStatus } from '@kovix/shell-kit/realtime';
import { useToast } from '@kovix/ui';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useCallback, useState } from 'react';
import { useSession } from '@/app/auth/session.store';
import { notifyKeys, useInbox } from '@/entities/notify/queries';
import { ShellIcon } from './NavIcon';
import { shellText } from './shellText';

/**
 * Chuông thông báo + trạng thái realtime (W11, TDD §9.2): đăng ký `/user/queue/inbox`; tin đến → làm mới hộp thư (TanStack Query) + toast tiêu đề.
 * Không lưu tin realtime vào store; số chưa đọc luôn từ API (`unread`).
 */
export function RealtimeBell() {
  const t = useT();
  const locale = useI18n((s) => s.locale);
  const qc = useQueryClient();
  const toast = useToast();
  const session = useSession();
  const status = useRealtimeStatus((s) => s.status);
  const [flash, setFlash] = useState(false);
  const { data } = useInbox({ unread: true, limit: 1 });
  const onInbox = useCallback(
    (m: RealtimeMessage) => {
      void qc.invalidateQueries({ queryKey: notifyKeys.all });
      const b = m.body as { title?: string | undefined; priority?: string | undefined } | null;
      if (b?.title) toast.push(b.title, b.priority === 'P1' ? 'error' : undefined);
      setFlash(true);
      setTimeout(() => setFlash(false), 1500);
    },
    [qc, toast],
  );
  useRealtime(session.status === 'ready' && session.hasModule('notify') ? realtimeDestinations.inbox() : null, onInbox);
  const unread = data?.unread ?? 0;
  // v152 (`.icon-btn` + `.v146-notify-count`): icon chuông nét, số chưa đọc đỏ góc trên phải; chấm nhỏ góc dưới = trạng thái kết nối realtime
  const label = [
    t('shell.notifications'),
    unread > 0 ? shellText('unread', locale, { n: unread }) : null,
    status === 'connected' ? null : t('shell.realtimeOffline'),
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <Link
      to="/notifications/inbox"
      search={{}}
      className={`kx-shell-icon-btn kx-shell-bell${flash ? ' flash' : ''}`}
      title={label}
      aria-label={label}
    >
      <ShellIcon name="bell" />
      {unread > 0 ? (
        <span className="kx-shell-bell-count" aria-hidden="true">
          {unread > 99 ? '99+' : unread}
        </span>
      ) : null}
      <span
        className={`kx-shell-bell-status ${status === 'connected' ? 'ok' : status === 'idle' ? 'idle' : 'warn'}`}
        aria-hidden="true"
      />
    </Link>
  );
}
